import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { getUserById } from '../api/user';
import { getSocials } from '../api/social';
import Cookies from 'js-cookie';

declare global {
  interface Window {
    google: any;
    onGoogleLibraryLoad: () => void;
  }
}

type GoogleLoginProps = {
  mode: 'login' | 'signup';
  onError?: (error: string) => void;
};

// Simple function to set tokens after Google auth
const setTokens = (authDto: any): void => {
  const { access, refresh } = authDto;
  Cookies.set('accessToken', access.token);
  Cookies.set('refreshToken', refresh.token);
  Cookies.set('accessTokenExpires', access.expires);
  Cookies.set('refreshTokenExpires', refresh.expires);
  localStorage.setItem('userId', access.userId.toString());
};

const GoogleLogin: React.FC<GoogleLoginProps> = ({ mode, onError }) => {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Initialize Google Sign-In when the component mounts
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    window.onGoogleLibraryLoad = () => {
      setIsLoaded(true);
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    window.google?.accounts.id.initialize({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    window.google?.accounts.id.renderButton(
      document.getElementById('googleLoginButton'),
      {
        type: 'standard',
        theme: 'filled_white',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        width: mode === 'login' ? 265 : 300,
        logo_alignment: 'center',
      }
    );
  }, [isLoaded]);

  const handleCredentialResponse = async (response: any) => {
    try {
      const idToken = response.credential;

      // Store the token for potential logout use
      localStorage.setItem('googleToken', idToken);

      try {
        // Call the backend with the Google ID token
        const authResponse = await API.post('/api/v1/identity/google-auth', {
          idToken: idToken,
        });

        if (authResponse.data) {
          // Set the authentication tokens
          setTokens(authResponse.data);

          // Get user info
          const userId = authResponse.data.access.userId;
          const user = await getUserById(userId);
          const socials = await getSocials(userId);
          localStorage.setItem('user', JSON.stringify(user));

          if (user) {
            // Redirect based on onboarded status
            if (!user.onboarded) {
              navigate('/onboarding');
            } else {
              navigate(`/global/settings`);
            }
          }
        }
      } catch (error: any) {
        console.error('Error during Google authentication:', error);

        if (error.response?.status === 403) {
          if (onError) {
            onError(
              'Your Google account could not be verified. Please try again.'
            );
          }
        } else {
          if (onError) {
            onError('Authentication failed. Please try again later.');
          }
        }
      }
    } catch (error) {
      console.error('Error processing Google sign-in:', error);
      if (onError) {
        onError('Failed to process Google sign-in. Please try again.');
      }
    }
  };

  return <div id="googleLoginButton" className="flex justify-center "></div>;
};

export default GoogleLogin;
